import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./MobilePreview.module.scss";

/**
 * MobilePreview component renders its children inside an iframe, allowing for a mobile preview of the content.
 * It disables all interactions inside except for scrolling of parent container
 * NOTE! It only wraps the children in a <main> tag - so to render a component that's not a top-level page,
 *       you need to wrap it yourself in the corresponding container elements of that component.
 */

export function MobilePreview({ children }: { children: React.ReactNode }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const copyStyles = () => {
      const iframeHead = iframe.contentDocument?.head;
      const parentStyles = document.querySelectorAll(
        "style, link[rel='stylesheet']",
      );

      parentStyles.forEach((style) => {
        // Check if the style already exists in the iframe's head to avoid duplication
        const existingStyle = Array.from(iframeHead?.children || []).find(
          (child) => child.isEqualNode(style),
        );
        if (!existingStyle) {
          iframeHead?.appendChild(style.cloneNode(true));
        }
      });
    };

    const handleLoad = () => {
      copyStyles();
      setReady(true);
    };

    iframe.addEventListener("load", handleLoad);

    // In case it's already loaded
    if (iframe.contentDocument?.readyState === "complete") {
      handleLoad();
    }

    return () => iframe.removeEventListener("load", handleLoad);
  }, []);

  return (
    <div className={styles.container}>
      <iframe ref={iframeRef} className={styles.iframe}>
        {ready &&
          iframeRef.current?.contentDocument &&
          iframeRef.current?.contentWindow &&
          createPortal(
            <main className={styles.iframeMain}>{children}</main>,
            iframeRef.current.contentDocument.body,
          )}
      </iframe>
    </div>
  );
}
